import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import { cn } from '@noor/lib/cn';
import { Button } from '../button';
import { Badge } from '../badge';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../command';
import { FilterClearButton } from './filter-clear-button';
import { useFiltersContext } from './filters-context';
import type { FiltersSelectOption } from './filters-select';

export interface FiltersMultiselectProps {
  paramKey: string;
  options: FiltersSelectOption[];
  label?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  className?: string;
}

const MAX_BADGES = 2;

/**
 * Multi-value filter. Selected values are serialized as a comma-joined list in
 * the URL (e.g. `?status=pending,delivered`).
 */
export function FiltersMultiselect({
  paramKey,
  options,
  label = 'Select',
  searchPlaceholder = 'Search…',
  emptyText = 'No results.',
  className,
}: FiltersMultiselectProps) {
  const { getListParam, setParam } = useFiltersContext();
  const selected = getListParam(paramKey);

  const toggle = (value: string): void => {
    const next = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    setParam(paramKey, next);
  };

  const selectedOptions = options.filter((o) => selected.includes(o.value));
  const hasValue = selected.length > 0;

  return (
    <Popover>
      <div className={cn('relative w-fit', className)}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            aria-label={label}
            className={cn(
              'h-10 justify-between gap-2 rounded-[24px] border-filter-border bg-filter-bg px-4 text-sm font-medium tracking-[-0.28px] text-filter-value-fg shadow-field hover:border-filter-border hover:bg-filter-bg hover:text-filter-value-fg has-[>svg]:px-4',
              hasValue && 'pr-9',
            )}
          >
            <span className="flex items-center gap-1">
              {selectedOptions.length === 0 ? (
                <span className="font-normal text-filter-label-fg">{label}</span>
              ) : (
                <>
                  {selectedOptions.slice(0, MAX_BADGES).map((o) => (
                    <Badge key={o.value} variant="secondary" className="rounded-sm">
                      {o.label}
                    </Badge>
                  ))}
                  {selectedOptions.length > MAX_BADGES && (
                    <Badge variant="secondary" className="rounded-sm">
                      +{selectedOptions.length - MAX_BADGES}
                    </Badge>
                  )}
                </>
              )}
            </span>
            {hasValue ? null : (
              <ChevronDownIcon className="size-5 text-muted-foreground" />
            )}
          </Button>
        </PopoverTrigger>
        {hasValue ? (
          <FilterClearButton
            onClear={() => setParam(paramKey, [])}
            aria-label={`${label} — clear`}
            className="absolute top-1/2 right-2.5 -translate-y-1/2"
          />
        ) : null}
      </div>
      <PopoverContent className="w-56 p-0" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = selected.includes(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    value={option.label}
                    onSelect={() => toggle(option.value)}
                  >
                    <span
                      className={cn(
                        'flex size-5 items-center justify-center rounded-[6px] border border-control-border bg-card',
                        isSelected
                          ? 'border-control-bg-checked bg-control-bg-checked text-control-fg-checked'
                          : 'opacity-50 [&_svg]:invisible',
                      )}
                    >
                      <CheckIcon className="size-3.5" />
                    </span>
                    {option.label}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
